import { useState, useEffect, useRef } from "react";
import { User, Volume2, Square, CalendarDays } from "lucide-react";
import { clsx } from "clsx";
import { motion } from "framer-motion";
import { extractAgentMatches, type ParsedAgentMatch } from "@/lib/agentCatalogue";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

interface AgentMeta {
  imageUrl?: string | null;
  role: string;
  description: string;
}

interface ChatMessageProps {
  role: "user" | "agent";
  content: string;
  agentLookup?: Record<string, AgentMeta>;
  onSuggestionClick?: (text: string) => void;
  onProceed?: (agents: Array<{ name: string; price: number }>) => void;
  onBrowseCatalogue?: () => void;
  onBookCall?: () => void;
  suppressAgentCards?: boolean;
}

function stripMarkdown(text: string) {
  return text
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
    .replace(/\{\{([^}]+)\}\}/g, "$1")
    .replace(/\[\[([^\]]+)\]\]/g, "$1");
}

function renderContent(text: string, onSuggestionClick?: (t: string) => void) {
  const processSegment = (segment: string, key: string): React.ReactNode[] => {
    const result: React.ReactNode[] = [];
    let idx = 0;
    let match: RegExpExecArray | null;
    // Group 1+2: markdown link, Group 3: raw URL, Group 4: bold, Group 5: [[suggestion]], Group 6: {{purple}}
    const combined = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)|(https?:\/\/[^\s]+)|\*\*([^*]+)\*\*|\[\[([^\]]+)\]\]|\{\{([^}]+)\}\}/g;
    combined.lastIndex = 0;

    while ((match = combined.exec(segment)) !== null) {
      if (match.index > idx) result.push(segment.slice(idx, match.index));
      if (match[1] && match[2]) {
        result.push(
          <a key={`${key}-link-${match.index}`} href={match[2]} target="_blank" rel="noopener noreferrer"
            className="text-green-600 underline underline-offset-2 font-semibold hover:text-green-700 transition-colors">
            {match[1]}
          </a>
        );
      } else if (match[3]) {
        result.push(
          <a key={`${key}-url-${match.index}`} href={match[3]} target="_blank" rel="noopener noreferrer"
            className="text-blue-600 underline underline-offset-2 hover:text-blue-700 transition-colors break-all">
            {match[3]}
          </a>
        );
      } else if (match[4]) {
        result.push(<strong key={`${key}-bold-${match.index}`}>{match[4]}</strong>);
      } else if (match[6]) {
        result.push(
          <span key={`${key}-purple-${match.index}`} className="text-violet-600 font-semibold">
            {match[6]}
          </span>
        );
      } else if (match[5]) {
        const suggestion = match[5];
        result.push(
          <button
            key={`${key}-sug-${match.index}`}
            onClick={() => onSuggestionClick?.(suggestion)}
            className="inline-flex items-center mx-0.5 px-3 py-1 rounded-full text-[12px] font-semibold bg-violet-600 text-white border border-violet-700 hover:bg-violet-700 active:scale-95 transition-all cursor-pointer leading-tight shadow-sm"
          >
            {suggestion}
          </button>
        );
      }
      idx = match.index + match[0].length;
    }
    if (idx < segment.length) result.push(segment.slice(idx));
    return result;
  };

  return text.split("\n").map((line, i, arr) => (
    <span key={i}>
      {processSegment(line, `line-${i}`)}
      {i < arr.length - 1 && <br />}
    </span>
  ));
}

function renderHighlighted(plain: string, charIndex: number): React.ReactNode {
  if (charIndex < 0) return plain;

  const wordRegex = /\S+/g;
  let wordStart = 0;
  let wordEnd = 0;
  let match: RegExpExecArray | null;

  while ((match = wordRegex.exec(plain)) !== null) {
    if (match.index <= charIndex) {
      wordStart = match.index;
      wordEnd = match.index + match[0].length;
    } else {
      break;
    }
  }

  return (
    <>
      {plain.slice(0, wordStart)}
      <span className="underline decoration-primary decoration-2 underline-offset-2 font-medium">
        {plain.slice(wordStart, wordEnd)}
      </span>
      {plain.slice(wordEnd)}
    </>
  );
}

function AgentMatchCard({
  match,
  meta,
  selected,
  onToggle,
}: {
  match: ParsedAgentMatch;
  meta?: AgentMeta;
  selected: boolean;
  onToggle: () => void;
}) {
  const imgSrc = meta?.imageUrl ? `${BASE}/api/storage${meta.imageUrl}` : null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      className={clsx(
        "flex flex-col gap-2 rounded-xl border px-4 py-3 w-full transition-opacity duration-200 backdrop-blur-sm",
        selected
          ? "border-cyan-400/40 opacity-100 bg-purple-950/40"
          : "border-cyan-400/10 opacity-40 bg-purple-950/20"
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          {/* Checkbox */}
          <button
            type="button"
            role="checkbox"
            aria-checked={selected}
            onClick={onToggle}
            className={clsx(
              "mt-0.5 shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors",
              selected
                ? "bg-cyan-400 border-cyan-400"
                : "bg-black/30 border-slate-600 hover:border-cyan-400"
            )}
          >
            {selected && (
              <svg viewBox="0 0 10 8" className="w-3 h-3 text-slate-950 fill-current" aria-hidden>
                <path d="M1 4l3 3 5-6" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            )}
          </button>

          {/* Agent avatar */}
          <div className="shrink-0 mt-0.5 w-9 h-9 rounded-lg overflow-hidden border border-cyan-400/30 bg-cyan-400/10 flex items-center justify-center">
            {imgSrc ? (
              <img src={imgSrc} alt={match.name} className="w-full h-full object-cover" />
            ) : (
              <span className="text-cyan-300 text-xs font-bold select-none">
                {match.name.slice(0, 2).toUpperCase()}
              </span>
            )}
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-bold text-[15px] text-white">{match.name}</span>
              {meta?.role && (
                <span className="text-[11px] text-slate-400 font-medium leading-tight">{meta.role}</span>
              )}
            </div>
            {meta?.description && (
              <p className="text-[12px] text-slate-400 leading-relaxed mt-0.5 line-clamp-2">{meta.description}</p>
            )}
          </div>
        </div>
        <span className="shrink-0 text-[13px] font-bold text-cyan-300 bg-cyan-400/10 border border-cyan-400/30 rounded-full px-3 py-1 leading-tight whitespace-nowrap">
          ${match.price.toLocaleString()}
        </span>
      </div>
    </motion.div>
  );
}

function AgentMatchGroup({
  matches,
  agentLookup,
  onProceed,
  onBrowseCatalogue,
}: {
  matches: ParsedAgentMatch[];
  agentLookup?: Record<string, AgentMeta>;
  onProceed?: (agents: Array<{ name: string; price: number }>) => void;
  onBrowseCatalogue?: () => void;
}) {
  const [selectedSet, setSelectedSet] = useState<Set<number>>(
    () => new Set(matches.map((_, i) => i))
  );

  const toggle = (i: number) => {
    setSelectedSet((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });
  };

  const selectedAgents = matches
    .filter((_, i) => selectedSet.has(i))
    .map((m) => ({ name: m.name, price: m.price }));

  const handleProceed = () => {
    if (!selectedAgents.length) return;
    onProceed?.(selectedAgents);
  };

  const label = "Yes, let's proceed!";

  return (
    <div className="flex flex-col gap-2">
      {matches.map((match, i) => (
        <AgentMatchCard
          key={`${match.name}-${i}`}
          match={match}
          meta={agentLookup?.[match.name]}
          selected={selectedSet.has(i)}
          onToggle={() => toggle(i)}
        />
      ))}
      <motion.div
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15, duration: 0.25 }}
        className="flex flex-wrap items-center gap-3 pt-1"
      >
        <button
          onClick={handleProceed}
          disabled={selectedAgents.length === 0}
          className="inline-flex items-center gap-2 bg-cyan-400 hover:bg-cyan-300 disabled:opacity-40 disabled:cursor-not-allowed text-slate-950 font-semibold text-[13px] px-5 py-2.5 rounded-full transition-colors active:scale-95"
          style={{ boxShadow: "0 0 18px rgba(0,229,255,0.35)" }}
        >
          {label}
        </button>
        {onBrowseCatalogue && (
          <button
            onClick={onBrowseCatalogue}
            className="inline-flex items-center gap-2 border border-cyan-400/60 text-cyan-300 hover:bg-cyan-400/10 font-semibold text-[13px] px-5 py-2.5 rounded-full transition-colors active:scale-95"
          >
            Browse more Agents
          </button>
        )}
      </motion.div>
    </div>
  );
}

export function ChatMessage({ role, content, agentLookup, onSuggestionClick, onProceed, onBrowseCatalogue, onBookCall, suppressAgentCards }: ChatMessageProps) {
  const isAgent = role === "agent";
  const [speaking, setSpeaking] = useState(false);
  const [wordCharIndex, setWordCharIndex] = useState(-1);
  const utteranceRef = useRef<SpeechSynthesisUtterance | null>(null);

  const plainText = stripMarkdown(content);

  // Parse agent match cards from agent messages
  const { matches, beforeText, afterText } = isAgent
    ? extractAgentMatches(content)
    : { matches: [], beforeText: content, afterText: "" };

  const hasMatches = matches.length > 0;

  useEffect(() => {
    return () => {
      window.speechSynthesis?.cancel();
    };
  }, []);

  const handleSpeak = () => {
    if (!window.speechSynthesis) return;

    if (speaking) {
      window.speechSynthesis.cancel();
      setSpeaking(false);
      setWordCharIndex(-1);
      return;
    }

    const utterance = new SpeechSynthesisUtterance(plainText);
    utterance.rate = 1.45;
    utterance.pitch = 1;

    utterance.onboundary = (e) => {
      if (e.name === "word") setWordCharIndex(e.charIndex);
    };
    utterance.onstart = () => setSpeaking(true);
    utterance.onend = () => { setSpeaking(false); setWordCharIndex(-1); };
    utterance.onerror = () => { setSpeaking(false); setWordCharIndex(-1); };

    utteranceRef.current = utterance;
    window.speechSynthesis.speak(utterance);
  };

  const isNoMatch = isAgent && content.includes("consultation call");

  const bodyContent = () => {
    if (!isAgent) return content;
    if (speaking) return renderHighlighted(plainText, wordCharIndex);

    if (hasMatches) {
      if (suppressAgentCards) {
        return (
          <div className="space-y-2">
            {beforeText && (
              <div className="text-slate-100 text-[15px] leading-relaxed">
                {renderContent(beforeText, onSuggestionClick)}
              </div>
            )}
            {afterText && (
              <div className="text-slate-100 text-[15px] leading-relaxed">
                {renderContent(afterText, onSuggestionClick)}
              </div>
            )}
          </div>
        );
      }
      return (
        <div className="space-y-3">
          {beforeText && (
            <div className="text-slate-100 text-[15px] leading-relaxed">
              {renderContent(beforeText, onSuggestionClick)}
            </div>
          )}
          <AgentMatchGroup matches={matches} agentLookup={agentLookup} onProceed={onProceed} onBrowseCatalogue={onBrowseCatalogue} />
          {afterText && (
            <div className="text-slate-100 text-[15px] leading-relaxed">
              {renderContent(afterText, onSuggestionClick)}
            </div>
          )}
        </div>
      );
    }

    if (isNoMatch && onBookCall) {
      return (
        <div className="space-y-4">
          <div className="text-slate-100 text-[15px] leading-relaxed">
            {renderContent(content, onSuggestionClick)}
          </div>
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.25 }}
          >
            <button
              onClick={onBookCall}
              className="inline-flex items-center gap-2 bg-cyan-400 hover:bg-cyan-300 text-slate-950 font-semibold text-[13px] px-5 py-2.5 rounded-full transition-colors active:scale-95"
              style={{ boxShadow: "0 0 18px rgba(0,229,255,0.35)" }}
            >
              <CalendarDays size={15} />
              Book a call
            </button>
          </motion.div>
        </div>
      );
    }

    return renderContent(content, onSuggestionClick);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className={clsx(
        "flex gap-4 p-6 rounded-2xl w-full max-w-3xl mx-auto my-2 backdrop-blur-sm",
        isAgent
          ? "bg-purple-950/30 border border-cyan-400/15"
          : "bg-cyan-400/5 border border-cyan-400/20"
      )}
    >
      <div className="w-8 h-8 rounded-full shrink-0 mt-0.5 shadow-sm overflow-hidden flex items-center justify-center bg-cyan-400">
        {isAgent ? (
          <img
            src={`${import.meta.env.BASE_URL}bluna.png`}
            alt="Bluna"
            className="w-full h-full object-cover object-top"
          />
        ) : (
          <User size={18} className="text-slate-950" />
        )}
      </div>

      <div className="flex-1 space-y-2 overflow-hidden min-w-0">
        <div className="flex items-center justify-between gap-2">
          <span className={clsx("font-semibold text-sm", isAgent ? "text-cyan-300" : "text-fuchsia-300")}>
            {isAgent ? "Bluna" : "You"}
          </span>
          {isAgent && window.speechSynthesis && (
            <button
              onClick={handleSpeak}
              title={speaking ? "Stop" : "Read aloud"}
              className={clsx(
                "shrink-0 p-1.5 rounded-full transition-colors",
                speaking
                  ? "text-primary bg-primary/10"
                  : "text-muted-foreground/40 hover:text-primary hover:bg-primary/10"
              )}
            >
              {speaking
                ? <Square size={14} fill="currentColor" className="animate-pulse" />
                : <Volume2 size={14} />}
            </button>
          )}
        </div>

        <div className="text-slate-100 text-[15px] leading-relaxed">
          {bodyContent()}
        </div>
      </div>
    </motion.div>
  );
}
